import {
  CatalogueProduct,
  CheckoutRequest,
  CreditPack,
  ProductKey,
} from "../types/billing";
import { clampCompanies, entityPricingOf } from "./productCatalogue";

/**
 * The arithmetic behind a purchase: what a number of runs costs, how many Credits it grants,
 * and the request that buys it. Kept apart from the dialog so the numbers can be checked
 * without a page around them, and so the later product pages quote the same way.
 *
 * Every amount here is whole currency units, as the backend sends them, so the sums are exact.
 */

/**
 * The most one checkout's total may be before tax, in whole units: the backend's cap, which
 * mirrors Stripe's 999,999.99 on a single charge divided by 1.27 and rounded down, so that tax
 * of up to 27% (the highest VAT rate in Europe) added on top at
 * checkout (prices are tax-exclusive) still fits under Stripe's cap. The backend refuses
 * anything above it with `BILLING_INVALID_QUANTITY`; the dialog stops short of it so the buyer is
 * told before the request rather than after. Keep it in step with the backend's value.
 */
export const MAX_CHECKOUT_AMOUNT = 787401;

/**
 * A run count as typed: a whole number of 1 or more, and nothing else — no sign, no decimals,
 * no exponent, nothing beyond what a number can hold exactly. `null` for anything that is not.
 */
export const parseRunCount = (text: string): number | null => {
  const trimmed = text.trim();
  if (!/^\d+$/.test(trimmed)) return null;
  const value = Number(trimmed);
  return Number.isSafeInteger(value) && value >= 1 ? value : null;
};

/** What a product purchase comes to, line by line. */
export interface ProductQuote {
  runs: number;
  /** Companies above the included number, on each run. Zero outside Multi-Entity SignalScan. */
  extraCompanies: number;
  runsAmount: number;
  /** `runs × extraCompanies`: the add-on units the checkout charges. */
  addonUnits: number;
  addonAmount: number;
  /** One unit grants exactly `run_credits`, one add-on unit exactly `extra_entity_credits`. */
  credits: number;
  total: number;
}

/**
 * The quote for `runs` runs of `product`, each carrying `companies` companies where that
 * applies. `null` when the product cannot be bought per product, or when the companies call
 * for an add-on it does not sell.
 */
export const quoteProductPurchase = (
  product: CatalogueProduct,
  runs: number,
  companies?: number,
): ProductQuote | null => {
  const purchase = product.purchase;
  if (!purchase) return null;

  const pricing = entityPricingOf(product);
  const extraCompanies = pricing
    ? clampCompanies(product, companies) - pricing.included
    : 0;
  if (extraCompanies > 0 && !purchase.addon) return null;

  const addonUnits = runs * extraCompanies;
  const runsAmount = runs * purchase.amount;
  const addonAmount = purchase.addon ? addonUnits * purchase.addon.amount : 0;
  const credits =
    runs * product.run_credits + addonUnits * (pricing?.extraCredits ?? 0);

  return {
    runs,
    extraCompanies,
    runsAmount,
    addonUnits,
    addonAmount,
    credits,
    total: runsAmount + addonAmount,
  };
};

/** How many runs of this size one checkout can take before it passes the checkout cap. */
export const maxRunsPerCheckout = (
  product: CatalogueProduct,
  companies?: number,
): number => {
  const one = quoteProductPurchase(product, 1, companies);
  return one ? Math.floor(MAX_CHECKOUT_AMOUNT / one.total) : 0;
};

/**
 * The body that buys `runs` runs of `product`. The add-on count goes only when there is one,
 * and `return_product` only when the dialog was opened from a product.
 */
export const productCheckoutRequest = (
  product: CatalogueProduct,
  runs: number,
  companies: number | undefined,
  returnProduct?: ProductKey,
): CheckoutRequest | null => {
  const quote = quoteProductPurchase(product, runs, companies);
  if (!quote || !product.purchase) return null;

  const request: CheckoutRequest = {
    lookup_key: product.purchase.lookup_key,
    quantity: quote.runs,
  };
  if (quote.extraCompanies > 0) {
    request.addon_quantity_per_unit = quote.extraCompanies;
  }
  if (returnProduct) request.return_product = returnProduct;
  return request;
};

/** The body that buys a pack: its key as it arrived, and nothing about a quantity. */
export const packCheckoutRequest = (
  pack: CreditPack,
  returnProduct?: ProductKey,
): CheckoutRequest =>
  returnProduct
    ? { lookup_key: pack.lookup_key, return_product: returnProduct }
    : { lookup_key: pack.lookup_key };

/* ------------------------------------------------------------ per-credit rates */

/** A price per credit, kept as the two whole numbers it comes from so comparing it is exact. */
export interface CreditRate {
  amount: number;
  credits: number;
}

export const rateValue = (rate: CreditRate): number =>
  rate.amount / rate.credits;

/** `a` costs less per credit than `b`, compared without dividing. */
export const isCheaperPerCredit = (a: CreditRate, b: CreditRate): boolean =>
  a.amount * b.credits < b.amount * a.credits;

/** The pack with the lowest price per credit, or `null` when there is none to compare. */
export const cheapestPackRate = (
  packs: readonly CreditPack[],
): CreditRate | null => {
  let cheapest: CreditRate | null = null;
  for (const pack of packs) {
    if (pack.credits <= 0) continue;
    const rate = { amount: pack.amount, credits: pack.credits };
    if (cheapest === null || isCheaperPerCredit(rate, cheapest))
      cheapest = rate;
  }
  return cheapest;
};

/**
 * What one run costs per credit when bought per product. The base run's rate, not the rate of
 * whatever size the dialog is set to, so the comparison with packs reads the same whichever
 * number of runs or companies is chosen.
 */
export const productRate = (product: CatalogueProduct): CreditRate | null =>
  product.purchase
    ? { amount: product.purchase.amount, credits: product.run_credits }
    : null;

/**
 * The two rates for "Credit packs cost less per credit", or `null` when packs are not cheaper
 * (or there is nothing to compare): the line is said only when it is true.
 */
export const packsCheaperThan = (
  packs: readonly CreditPack[],
  product: CatalogueProduct,
): { pack: CreditRate; product: CreditRate } | null => {
  const pack = cheapestPackRate(packs);
  const own = productRate(product);
  if (!pack || !own || !isCheaperPerCredit(pack, own)) return null;
  return { pack, product: own };
};
